import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Page, PageHeader } from '@tanium/react-page';
import { Box } from '@tanium/react-box';
import { Shimmer } from '@tanium/react-shimmer';
import { useTaniumInstances } from '../../hooks/useTaniumInstances';
import { useAuthContext } from '../../auth';
import { getArtifactsLink } from '../../utilities/instanceHelper';
import { AsyncSuspense } from '../../components/AsyncSuspense';
import { useTokenReloaderEffect } from '../../auth/useTokenReloaderCallbacks';
import { AmpAccessToken } from '../../auth/Auth';
import { IframeContainer } from '../CloudManagement/CMP';

enum InstanceType {
  Cloud = 'cloud',
  OnPrem = 'on-prem',
}

export const Artifacts: React.VFC = () => {
  const { t } = useTranslation();
  const auth = useAuthContext();
  const { linkedTaniumInstances, loadLinkedTaniumInstances } = useTaniumInstances();
  const [iframeLoaded, setIframeLoaded] = React.useState(false);

  const artifactsUrl = useMemo(() => {
    if (window.location.host.startsWith('localhost')) {
      return 'http://localhost:3000/#/';
    }

    return getArtifactsLink();
  }, []);

  const linkedInstanceTypes = useMemo(() => {
    const instanceSet = new Set<InstanceType>();
    linkedTaniumInstances.forEach((inst) => {
      instanceSet.add(inst.cloudProvider ? InstanceType.Cloud : InstanceType.OnPrem);
    });
    return instanceSet;
  }, [linkedTaniumInstances]);

  // TODO(TSP-1788): Remove when artifacts is explicitly requesting token
  const tokenCallback = useCallback(
    (token: AmpAccessToken) => {
      if (!iframeLoaded) {
        return false;
      }
      const iframe = document.querySelector('iframe');
      const handle = iframe!.contentWindow;

      // NOTE, the following is a hack, where the content security policy will block
      // AMP from checking the origin once the iframe content has loaded.
      // postMessage is asynchronous and cannot catch an error, so this gives a
      // quick way to ensure that the iframe is actually loaded.
      try {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        handle!.origin;
        // eslint-disable-next-line no-empty
      } catch (_) {}

      const message = {
        jwt: token.token,
        linkedInstanceTypes: [...linkedInstanceTypes],
      };
      let times = 0;
      const intervalId = setInterval(() => {
        handle!.postMessage(message, artifactsUrl);
        times++;
        // 20 times per second for 10 seconds
        if (times >= 20 * 10) {
          clearInterval(intervalId);
        }
      }, 50);
      return true;
    },
    [artifactsUrl, iframeLoaded, linkedInstanceTypes]
  );

  useTokenReloaderEffect(auth, tokenCallback);

  return (
    <IframeContainer>
      <Page header={<PageHeader heading={''} />}>
        <AsyncSuspense
          state={loadLinkedTaniumInstances}
          loading={
            <Box mt={2} height={1} width="100%">
              <Shimmer />
            </Box>
          }
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          failure={t('Failed to load Tanium Instances')}
        >
          <iframe
            id="artifactsIframe"
            data-testid="artifactsIframe"
            // eslint-disable-next-line @tanium/m6web-i18n/no-text-as-attribute
            title="Artifacts"
            src={artifactsUrl}
            width="100%"
            style={{
              height: 'calc(100% + 300px)',
              border: 'none',
            }}
            onLoad={() => {
              setIframeLoaded(true);
            }}
          />
        </AsyncSuspense>
      </Page>
    </IframeContainer>
  );
};
